import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [adminExpanded, setAdminExpanded] = useState(location.pathname.startsWith('/admin'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      path: '/',
      icon: 'fas fa-home',
      label: 'Home',
      active: location.pathname === '/'
    },
    {
      path: '/study-pack',
      icon: 'fas fa-book-open',
      label: 'Study Pack',
      active: location.pathname === '/study-pack'
    },
    {
      path: '/progress',
      icon: 'fas fa-chart-line',
      label: 'Progress',
      active: location.pathname === '/progress'
    },
    {
      path: '/prep',
      icon: 'fas fa-graduation-cap',
      label: 'Prep',
      active: location.pathname === '/prep'
    },
    {
      path: '/mock-test',
      icon: 'fas fa-file-alt',
      label: 'Mock Test',
      active: location.pathname === '/mock-test'
    },
  ];

  const adminMenuItem = {
    path: '/admin',
    icon: 'fas fa-user-shield',
    label: 'Admin',
    active: location.pathname.startsWith('/admin')
  };

  const adminSubMenuItems = [
    {
      path: '/admin',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      active: location.pathname === '/admin'
    },
    {
      path: '/admin/users',
      icon: 'fas fa-users',
      label: 'Users',
      active: location.pathname.startsWith('/admin/users')
    },
    {
      path: '/admin/questions',
      icon: 'fas fa-question-circle',
      label: 'Questions',
      active: location.pathname === '/admin/questions' && !location.pathname.includes('/stats') && !location.pathname.includes('/bulk-import')
    },
    {
      path: '/admin/questions/stats',
      icon: 'fas fa-chart-bar',
      label: 'Statistics',
      active: location.pathname.includes('/admin/questions/stats')
    },
    {
      path: '/admin/questions/bulk-import',
      icon: 'fas fa-file-upload',
      label: 'Bulk Import',
      active: location.pathname.includes('/admin/questions/bulk-import')
    }
  ];

  return (
    <>
      <div style={{
        ...styles.sidebar,
        ...(isOpen ? styles.sidebarOpen : styles.sidebarClosed)
      }}>
        <div style={styles.sidebarHeader}>
          <div style={styles.sidebarLogo}>
            <div style={styles.logoIcon}>
              <i className="fas fa-graduation-cap"></i>
            </div>
            {isOpen && (
              <div style={styles.logoText}>
                <div style={styles.logoTitle}>Cognify Analytics</div>
                <div style={styles.logoSubtitle}>Smart Learning Portal</div>
              </div>
            )}
          </div>
        </div>

        <nav style={styles.navigation}>
          <div style={styles.menuSection}>
            {isOpen && <div style={styles.menuLabel}>MAIN MENU</div>}
            <ul style={styles.menuList}>
              {menuItems.map((item, index) => (
                <li key={index} style={styles.menuItem}>
                  <Link 
                    to={item.path} 
                    style={{
                      ...styles.menuLink,
                      ...(item.active ? styles.menuLinkActive : {}),
                      ...(!isOpen ? styles.menuLinkCollapsed : {})
                    }}
                    title={!isOpen ? item.label : ''}
                    onClick={() => window.innerWidth <= 768 && toggleSidebar()}
                  >
                    <i className={item.icon} style={styles.menuIcon}></i>
                    {isOpen && <span style={styles.menuText}>{item.label}</span>}
                    {isOpen && item.active && (
                      <div style={styles.activeIndicator}></div>
                    )}
                  </Link>
                </li>
              ))}
              
              {isAdmin && (
                <>
                  {isOpen && <div style={{...styles.menuLabel, marginTop: '20px'}}>ADMINISTRATION</div>}
                  <li style={styles.menuItem}>
                    <div 
                      style={{
                        ...styles.menuLink,
                        ...(adminMenuItem.active ? styles.menuLinkActive : {}),
                        ...(!isOpen ? styles.menuLinkCollapsed : {}),
                        cursor: 'pointer'
                      }}
                      title={!isOpen ? adminMenuItem.label : ''}
                      onClick={() => {
                        if (isOpen) {
                          setAdminExpanded(!adminExpanded);
                        } else {
                          navigate('/admin');
                          if (window.innerWidth <= 768) toggleSidebar();
                        }
                      }}
                    >
                      <i className={adminMenuItem.icon} style={styles.menuIcon}></i>
                      {isOpen && <span style={styles.menuText}>{adminMenuItem.label}</span>}
                      {isOpen && (
                        <i className={`fas fa-chevron-${adminExpanded ? 'down' : 'right'}`} style={styles.chevronIcon}></i>
                      )}
                    </div>
                  </li>
                  
                  {isOpen && adminExpanded && (
                    <ul style={styles.subMenuList}>
                      {adminSubMenuItems.map((item, index) => (
                        <li key={index} style={styles.subMenuItem}>
                          <Link 
                            to={item.path} 
                            style={{
                              ...styles.subMenuLink,
                              ...(item.active ? styles.subMenuLinkActive : {})
                            }}
                            onClick={() => window.innerWidth <= 768 && toggleSidebar()}
                          >
                            <i className={item.icon} style={styles.subMenuIcon}></i>
                            <span style={styles.subMenuText}>{item.label}</span>
                            {item.active && (
                              <div style={styles.activeIndicator}></div>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </ul>
          </div>

          {isOpen && (
            <div style={styles.supportSection}>
              <div style={styles.supportCard}>
                <div style={styles.supportIcon}>
                  <i className="fas fa-headset"></i>
                </div>
                <div style={styles.supportContent}>
                  <div style={styles.supportTitle}>Need Help?</div>
                  <div style={styles.supportText}>Contact our support team</div>
                </div>
              </div>
            </div>
          )}
          
          <div style={styles.spacer}></div>
          
          <div style={styles.footerSection}>
            <button 
              style={{
                ...styles.logoutButton,
                ...(!isOpen ? styles.logoutButtonCollapsed : {})
              }} 
              onClick={handleLogout}
              title={!isOpen ? 'Logout' : ''}
            >
              <i className="fas fa-sign-out-alt" style={styles.logoutIcon}></i>
              {isOpen && <span style={styles.logoutText}>Logout</span>}
            </button>
          </div>
        </nav>
      </div>

      {isOpen && window.innerWidth <= 768 && (
        <div style={styles.overlay} onClick={toggleSidebar}></div>
      )}
    </>
  );
};

const styles = {
  sidebar: {
    backgroundColor: '#ffffff',
    width: '280px',
    minHeight: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
    transition: 'all 0.3s ease',
    borderRight: '1px solid #e5e7eb',
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarOpen: {
    transform: 'translateX(0)',
    width: '280px',
  },
  sidebarClosed: {
    transform: 'translateX(0)',
    width: '70px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 20px',
    borderBottom: '1px solid #f3f4f6',
    minHeight: '80px',
    marginTop: '70px', 
  },
  sidebarLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#4f46e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '18px',
    flexShrink: 0,
  },
  logoText: {
    display: 'flex',
    flexDirection: 'column',
  },
  logoTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#111827',
    lineHeight: '1.2',
  },
  logoSubtitle: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.2',
  },
  navigation: {
    flex: 1,
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  menuSection: {
    marginBottom: '24px',
  },
  menuLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '0 20px 12px',
    marginBottom: '8px',
    borderBottom: '1px solid #f3f4f6',
  },
  menuList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  menuItem: {
    marginBottom: '4px',
  },
  menuLink: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    color: '#6b7280',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    position: 'relative',
    borderLeft: '3px solid transparent',
  },
  menuLinkActive: {
    backgroundColor: '#f8fafc',
    color: '#4f46e5',
    borderLeftColor: '#4f46e5',
  },
  menuLinkCollapsed: {
    padding: '12px',
    justifyContent: 'center',
  },
  menuIcon: {
    fontSize: '16px',
    width: '20px',
    marginRight: '12px',
    flexShrink: 0,
  },
  menuText: {
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },
  activeIndicator: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#4f46e5',
    marginLeft: '8px',
  },
  chevronIcon: {
    fontSize: '10px',
    marginLeft: 'auto',
    color: '#9ca3af',
  },
  subMenuList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 8px 0',
    backgroundColor: '#f9fafb',
    borderLeft: '2px solid #e5e7eb',
    marginLeft: '20px',
  },
  subMenuItem: {
    marginBottom: '2px',
  },
  subMenuLink: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px 10px 30px',
    color: '#6b7280',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    position: 'relative',
    fontSize: '13px',
  },
  subMenuLinkActive: {
    color: '#4f46e5',
    backgroundColor: '#eff6ff',
  },
  subMenuIcon: {
    fontSize: '14px',
    width: '16px',
    marginRight: '10px',
    flexShrink: 0,
  },
  subMenuText: {
    fontSize: '13px',
    fontWeight: '500',
    flex: 1,
  },
  supportSection: {
    padding: '0 20px',
    marginBottom: '24px',
  },
  supportCard: {
    backgroundColor: '#eff6ff',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #dbeafe',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  supportIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '14px',
    flexShrink: 0,
  },
  supportContent: {
    flex: 1,
  },
  supportTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e40af',
    lineHeight: '1.2',
    marginBottom: '2px',
  },
  supportText: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.2',
  },
  spacer: {
    flex: 1,
  },
  footerSection: {
    padding: '20px',
    borderTop: '1px solid #f3f4f6',
  },
  logoutButton: {
    width: '100%',
    backgroundColor: 'transparent',
    border: '1px solid #dc2626',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    fontWeight: '500',
  },
  logoutButtonCollapsed: {
    padding: '12px',
    justifyContent: 'center',
  },
  logoutIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  logoutText: {
    flex: 1,
    textAlign: 'center',
    fontWeight: '600',
  },
};

export default Sidebar;